import styles from "./Calculator.module.scss";
import { CalculatorProps } from "./Calculator.types";
import { ChangeEvent, useState, useRef } from "react";
import Slider from "react-rangeslider";
import "react-rangeslider/lib/index.css";
import { useApiContext } from "../../contexts/ApiContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setBalance } from "../../app/reducers/balance/balance.slice";
import { ModalsTypesEnum } from "../../app/reducers/modal/modal.types";
import { setCurrentModal } from "../../app/reducers/modal/modal.slice";
import ColorCard from "../SAIRUS_NEW/ColorCard/ColorCard";
import { setError } from "../../app/reducers/error/error.slice";

export default function Calculator(props: CalculatorProps) {
    const [value, setValue] = useState<number>(props.min);
    const [timer, setTimer] = useState<ReturnType<typeof setTimeout>>();
    const { isAuth, user, response, updateUser } = useApiContext();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);

    const location = useLocation();

    const sliderHandler = (val: number) => {
        setValue(val);
    };

    const { balance } = useAppSelector((state) => state.balance);

    const dispatch = useAppDispatch();

    const inputHandler = (event: ChangeEvent<HTMLInputElement>) => {
        let val = event.target.valueAsNumber || 0;
        console.log(val);
        setValue(val);
        if (inputRef) {
            if (inputRef.current) {
                inputRef.current.valueAsNumber = inputRef.current ? value : 0;
            }
        }

        if (timer) clearTimeout(timer);
        if (val < props.min) setTimer(setTimeout(endInput, 1200));
        if (val > props.max) setTimer(setTimeout(endInput, 1200));
    };

    const endInput = () => {
        if (value < props.min) {
            setValue(props.min);
        } else if (value > props.max) {
            setValue(props.max);
        } else {
            if (inputRef) {
                if (inputRef.current) {
                    inputRef.current.value = "";
                    inputRef.current.valueAsNumber = inputRef.current ? value : 0;
                }
            }
        }
    };

    const save = async () => {
        if (!isAuth) {
            navigate("/login");
            return;
        }
        if (!location.pathname.includes("/account/balance")) {
            navigate("/account/balance");
            return;
        }

        if (user && user.balances && user.balances.length > 0 && Number(user.balances[0].amount) >= value) {
            const data = {
                amount: value,
                type: props.type || "MEGA",
            };
            const res = await response("/deposit/save", "POST", JSON.stringify(data));

            if (res.code === "OK") {
                dispatch(setBalance(balance - value));
                dispatch(setCurrentModal(ModalsTypesEnum.DEPOSIT_CREATED));
                await updateUser();
            } else {
                dispatch(setError(res.message));
            }
        } else dispatch(setCurrentModal(ModalsTypesEnum.REPLENISH));
    };

    const getProfitWrapperStyles = (color: string): string => {
        const result = [styles.profit_wrapper];
        switch (color) {
            case "magenta":
                result.push(styles.profit_wrapper_magenta);
                break;
            case "purple":
                result.push(styles.profit_wrapper_purple);
                break;
            case "cian":
                result.push(styles.profit_wrapper_cian);
                break;
            default:
                result.push(styles.profit_wrapper_magenta);
                break;
        }
        return result.join(" ");
    };

    return (
        <ColorCard title={props.type} color={props.color} buttonLabel={t("calculator.invest") || ""} onClick={save}>
            <div className={styles.amountAndSum}>
                <div>
                    <p className={styles.textS}>{t("calculator.income-per-day")}</p>
                    <span className={styles.rate}>{props.rate.toFixed(1)}%</span>
                </div>
                <div>
                    <p className={[styles.textS, styles.amount].join(" ")}>{t("calculator.amount-of-investment")}:</p>
                    <div>
                        <div className={styles.inputContainer}>
                            <div className={styles.inputSmall}>
                                <input
                                    type="number"
                                    className={styles.inputText}
                                    onChange={inputHandler}
                                    value={value}
                                    ref={inputRef}
                                ></input>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.content}>
                <Slider
                    value={value}
                    min={props.min}
                    max={props.max}
                    onChange={sliderHandler}
                    className={`swiper-no-swiping ${props.color}`}
                />
                <div className={styles.limits}>
                    <span className="text-xs">
                        {props.min} {t("currency")}
                    </span>
                    <span className="text-xs">
                        {props.max} {t("currency")}
                    </span>
                </div>
                <div className={getProfitWrapperStyles(props.color)}>
                    <div className={styles.profit}>
                        <span>{t("calculator.profit-in-24-hours")}</span>
                        <span>
                            {(value * (props.rate / 100)).toFixed(1)} {t("currency")}
                        </span>
                    </div>
                    <div className={styles.profit}>
                        <span>{t("calculator.profit-per-week")}</span>
                        <span>
                            {(value * (props.rate / 100) * 7).toFixed(1)} {t("currency")}
                        </span>
                    </div>
                    <div className={styles.profit}>
                        <span>{t("calculator.profit-per-month")}</span>
                        <span>
                            {(value * (props.rate / 100) * 30).toFixed(1)} {t("currency")}
                        </span>
                    </div>
                    <div className={styles.profit}>
                        <span>{t("calculator.profit-per-year")}</span>
                        <span>
                            {(value * (props.rate / 100) * 356).toFixed(1)} {t("currency")}
                        </span>
                    </div>
                </div>
            </div>
        </ColorCard>
    );
}
